/**
 * Browser log backend that POSTs to this app's own `/api/log` Worker route.
 *
 * The browser never talks to the sink directly: an Axiom write token belongs
 * to a backend, never to a browser bundle. The Worker route
 * (apps/retells/worker/api-log.ts.example, inert until a project activates it)
 * holds the token as a Cloudflare Worker secret and forwards validated
 * events server-side.
 *
 * This POSTs `toAxiomEvent(entry)` — the canonical wide-event shape —
 * wrapped in a one-element array, matching the route's `Array.isArray(body)`
 * batch contract (batches of any size >= 1). No client-side batching: every
 * `write()` call is its own POST, immediately, with `keepalive: true` so the
 * request survives `pagehide` / unload (logging-strategy POLICY.md §8). Real
 * batching (queue + debounce + `pagehide` flush) is a deliberate scope cut.
 */

import { toAxiomEvent } from "../axiom-event";
import type { BaseLogEntry } from "../types";
import type { LogBackend } from "./interface";
import { logDevError } from "./log-dev-error";

export class WorkerLogBackend implements LogBackend {
  private readonly endpoint: string;

  constructor(endpoint = "/api/log") {
    this.endpoint = endpoint;
  }

  write(entry: BaseLogEntry): void {
    try {
      const body = JSON.stringify([toAxiomEvent(entry)]);
      void fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch((err: unknown) => {
        logDevError("WorkerLogBackend", "fetch", err);
      });
    } catch (err: unknown) {
      // JSON.stringify can throw on circular refs — never let the app
      // crash because of a log write.
      logDevError("WorkerLogBackend", "serialize", err);
    }
  }

  /**
   * No-op: this backend has no buffer to drain (no client-side batching).
   * Present so callers that flush every configured backend (e.g. on
   * `visibilitychange`) don't need to special-case this one.
   */
  async flush(): Promise<void> {
    // Nothing buffered — every write() already sent its own request.
  }
}
